import React from 'react';
import { InfoIcon, Table, Tooltip } from '@exxonmobil/react-unity';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { DomainValidation } from '../../common/types/DomainTypes';
import { ErrorBox } from '../../common/components/ErrorBox';
import { numberMillify, percentNumberFormat } from '../../common/utils/numberUtil';
import { StatusBadge } from '../../common/components/StatusBadge';
import { TableBodyRow, TableCell, TableHeadRow } from '../../common/components/TableComponents';
import { DataTableLoading } from '../../common/components/TableLoading';

interface DomainTableHeader {
  title: string;
  description?: string;
}

interface Props {
  domains: DomainValidation[];
  isLoading: boolean;
  isError: boolean;
  domainTableHeaders: DomainTableHeader[];
  onRefresh: () => void;
}

const DataHeaders = ({ domainTableHeaders }: { domainTableHeaders: DomainTableHeader[] }) => (
  <StyledTableHeader>
    <StyledTableHeadRow>
      {domainTableHeaders.map((header, idx) => (
        <StyledTableHeadCell key={idx}>
          <Tooltip className="em-u-margin-half" variant="above" color="light">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip.Passage> {header.title}</Tooltip.Passage>
              {header.description && <StyledTooltipContent>{header.description}</StyledTooltipContent>}
              {header.description && (
                <StyledInfoIcon>
                  <InfoIcon size="small" />
                </StyledInfoIcon>
              )}
            </div>
          </Tooltip>
        </StyledTableHeadCell>
      ))}
    </StyledTableHeadRow>
  </StyledTableHeader>
);

const DataRows = ({ domains }: { domains: DomainValidation[] }) => (
  <Table.Body>
    {domains.map((domain) => (
      <TableBodyRow key={domain.id}>
        <TableCell>
          <StyledLink to={`./detail/${domain.id}`} style={{ lineHeight: '40px' }}>
            <TextContainer>{domain.name}</TextContainer>
          </StyledLink>
        </TableCell>
        <TableCell>
          <StatusBadge status={domain.status} />
        </TableCell>
        <TableCell>{percentNumberFormat(domain.availability)}</TableCell>
        <TableCell>{numberMillify(domain.eventUsage)}</TableCell>
        <TableCell>{numberMillify(domain.apiUsage)}</TableCell>
      </TableBodyRow>
    ))}
  </Table.Body>
);

const DataTable = ({ domains, isLoading, isError, domainTableHeaders, onRefresh }: Props) => (
  <StyledTable striped className="data-table">
    <DataHeaders domainTableHeaders={domainTableHeaders} />
    {isError && <ErrorBox isRefreshable onRefresh={onRefresh} />}
    {isLoading && <DataTableLoading rowNumber={5} columnSpan={1} />}
    <DataRows domains={domains} />
  </StyledTable>
);

const StyledTable = styled(Table)`
  border: '1px solid #e5e5e5';
  & .em-c-table__header {
    background: #f5f5f5 !important;
  }
`;

const StyledLink = styled(Link)`
  F &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
    color: black;
  }
  display: flex;
  flex-direction: row;
`;

const TextContainer = styled.p`
  line-height: 36px;
  margin-bottom: 0;
`;

const StyledTableHeader = styled(Table.Head)`
  height: 150px;
`;

const StyledTableHeadRow = styled(TableHeadRow)`
  height: 2px;
  margin-top: 110px;
  & .em-c-table__header-cell {
    padding: 1.2rem 0.8rem;
  }
`;

const StyledTableHeadCell = styled(Table.Head.Cell)`
  background: #112;
  & .em-c-text-passage {
    font-size: 0.75rem;
    color: white;
  }
  & .em-u-margin-half {
    margin: 0 !important;
  }
`;

const StyledTooltipContent = styled(Tooltip.Content)`
  font-size: 12px;
  text-transform: none;
`;

const StyledInfoIcon = styled(InfoIcon)`
  color: #f5f5f5;
  height: 6%;
  width: 6%;
  margin-right: auto;
  margin-bottom: auto;
`;

export default DataTable;
